import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { Layout } from 'antd';
import { PaginationProvider } from '../src/context/PaginationProvider';
import MainLayout from './components/Shared/MainLayout';
import LandingPage from './pages/LandingPage';
import ReportsPage from './pages/ReportsPage';
import UploadPage from './pages/UploadPage';
import UserList from './pages/UserList';
import AddEditUser from './components/AddEditUser';
import LoginPage from './pages/LoginPage';
import ResetPWD from './components/ResetPWD';
import PrivateRoute from './routes/PrivateRoute';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import InvProvider from '../src/context/InvProvider';
import { AuthProvider } from './context/AuthContext';
import './App.css';
import AddEditRole from './components/AddEditRole';
import SingleInvoiceEdit from './components/SingleInvoiceEdit';
import RoleList from './pages/RoleList';
import ModificationLog from './components/ModificationLog';
import VendorEdit from './components/VendorEdit';
import ViewInvoice from './components/ViewInvoice';
import ToleranceUpdate from './pages/toleranceUpdate';
import ToleranceList from './pages/ToleranceList';
import ToleranceModificationLog from './components/ToleranceModificationLog';
import AddressList from './pages/AddressList';

const App: React.FC = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <Layout className="pageLayout" style={{ minHeight: '100vh' }}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider> {/* Wrapping the whole app with AuthProvider */}
          <InvProvider>
            <PaginationProvider>
              <Routes>
                {/* Public Route for login */}
                <Route path="/login" element={<LoginPage />} />

                {/* Protected routes with MainLayout */}
                <Route path="/" element={<LoginPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <LandingPage />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/invoice-list"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ReportsPage />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/upload"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <UploadPage />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/user-list"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <UserList />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/user-list/add"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <AddEditUser />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/user-list/edit/:id"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <AddEditUser />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />

                {/* Role routes */}
                <Route
                  path="/role-list"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <RoleList />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/role-list/add"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <AddEditRole />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/role-list/edit/:id"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <AddEditRole />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ResetPWD />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/invoice-edit"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <SingleInvoiceEdit />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/modification-log"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ModificationLog />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/upload"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <UploadPage />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/vendor-edit/verify"
                  element={
                    <MainLayout>
                      <VendorEdit />
                    </MainLayout>
                  }
                />
                <Route
                  path="/invoice-view"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ViewInvoice />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/toleranceUpdate/:id"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ToleranceUpdate />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tolerance-modificationlog/:id"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ToleranceModificationLog />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tolerance-list"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <ToleranceList />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/address-list"
                  element={
                    <PrivateRoute>
                      <MainLayout>
                        <AddressList />
                      </MainLayout>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </PaginationProvider>
          </InvProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Layout>
  );
};

export default App;
